/**
 * Overlay — JEDEN rdzeń zachowania wszystkich okien nakładkowych w aplikacji.
 *
 * `Modal`, `Drawer` i `PreviewModal` to cienkie „skórki" (wygląd i układ) na tym komponencie.
 * Używaj `Overlay` bezpośrednio TYLKO dla udokumentowanych wyjątków, które muszą wyglądać inaczej
 * niż zwykłe okno (kreatory pełnoekranowe, lightbox, mapa obszarów) — zachowanie dostają to samo:
 *
 *   - portal do `<body>`, tło `role="presentation"` + panel z `role="dialog"` / `"alertdialog"`;
 *   - zamykanie kliknięciem w tło tylko gdy wciśnięcie I puszczenie myszy były na tle
 *     (`useBackdropClose`), bez „przypadkowych" zamknięć przy zaznaczaniu tekstu w oknie;
 *   - Esc: zamyka okno z wierzchu. Esc obsłużony już przez element wewnątrz (np. otwartą listę
 *     Radix, `preventDefault`) nie zamyka okna. Okno zatrzymuje Esc ZAWSZE (także przy
 *     `closeOnEsc={false}`), żeby nie zamknął się rodzic ani nie zadziałał skrót strony pod spodem.
 *     JEDYNY WYJĄTEK: w trakcie przeciągania dnd-kit w tym oknie okno Esc przepuszcza i się nie
 *     zamyka — dnd-kit słucha go na `document` i anuluje nim przeciąganie. „W tym oknie" =
 *     AKTYWNY UCHWYT leży w panelu okna albo w portalu zarejestrowanym przez komponent
 *     w oknie (popover z listą uchwytów: `useOverlayPortalScopeRef`) — niezależnie od tego,
 *     skąd przyszło zdarzenie (fokus mógł się przenieść z uchwytu). Przeciąganie poza oknem
 *     albo w niezarejestrowanym portalu nie odbiera oknu Esc. Z tej samej reguły: dopóki trwa
 *     takie przeciąganie, klik w tło okna go nie zamyka (zamknięcie zostawiłoby czujnik dnd-kit
 *     aktywny na `document`);
 *   - pułapka fokusu (Tab), fokus początkowy na pierwszym polu (z poszanowaniem `autoFocus`),
 *     powrót fokusu do wyzwalacza po zamknięciu;
 *   - blokada przewijania strony z licznikiem (bezpieczna przy kilku oknach naraz);
 *   - opcjonalne zabezpieczenie niezapisanych zmian (`guardUnsaved`).
 *
 * Okno jest montowane tylko, gdy `open` — każde otwarcie startuje ze świeżym stanem.
 */

import {
  type KeyboardEvent,
  type ReactNode,
  type SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { useBackdropClose } from '@/hooks/useBackdropClose';
import {
  OverlayPortalScopeContext,
  type RegisterOverlayPortal,
} from '@/hooks/useOverlayPortalScope';
import {
  acquireBodyScrollLock,
  FOCUSABLE_SELECTOR,
  isDragInProgressInScopes,
  registerOverlay,
} from '@/lib/overlay-stack';

export interface OverlayProps {
  open: boolean;
  onClose: () => void;
  closeOnBackdrop?: boolean;
  closeOnEsc?: boolean;
  role?: 'dialog' | 'alertdialog';
  /** Klasy tła BEZ `fixed inset-0` (to dokłada rdzeń): z-index, układ, kolor tła, odstępy. */
  backdropClassName?: string;
  /** Klasy panelu. */
  panelClassName?: string;
  labelledBy?: string;
  describedBy?: string;
  /** Etykieta dla okna bez widocznego tytułu. */
  ariaLabel?: string;
  /** `data-testid` na tle (zawiera cały panel — zapytania `within(...)` działają). */
  testId?: string;
  /** `data-testid` na panelu. */
  panelTestId?: string;
  /**
   * Zabezpieczenie niezapisanych zmian: gdy użytkownik coś wpisał i zamyka okno kliknięciem
   * w tło lub Esc, pytamy „Porzucić wprowadzone zmiany?". X, „Anuluj", zapis i zamknięcie
   * sterowane przez rodzica (`open=false`) nie pytają. Włączaj w oknach, w których zamknięcie
   * traci pracę, której nie da się odtworzyć jednym ruchem (duży formularz, kreator, edytor).
   * Zmiany wykrywamy po zdarzeniach wpisywania w polach PANELU okna (zdarzenia z portali, np.
   * pole wyszukiwania otwartej listy, są pomijane), a pole z `data-no-dirty` jest pomijane
   * jawnie. Wybór kliknięciem (lista rozwijana poza `<form>`, karta, przycisk „Dodaj wiersz")
   * nie wysyła takich zdarzeń — w oknach, gdzie dane powstają kliknięciem, przekaż `dirty`.
   */
  guardUnsaved?: boolean;
  /** Jawna informacja „są niezapisane zmiany" — nadpisuje wykrywanie automatyczne. */
  dirty?: boolean;
  /**
   * Zmiana tej wartości zeruje automatycznie wykryte „coś wpisano". Dla okien, które po udanym
   * zapisie zostają otwarte (np. edycja osadzona): rodzic zmienia klucz przy zapisie, żeby
   * zamknięcie zaraz po zapisie nie pytało o zmiany, które już zapisano. Uwaga: zeruje też
   * zmiany wpisane między wysłaniem zapisu a odpowiedzią — zmieniaj klucz dopiero po sukcesie
   * i tylko w oknach, których pola nie da się edytować w trakcie zapisu.
   */
  guardResetKey?: string | number;
  children?: ReactNode;
}

export function Overlay(props: OverlayProps) {
  return props.open ? <OverlayMounted {...props} /> : null;
}

/**
 * Zakresy „tego okna" dla uchwytu przeciągania dnd-kit: panel i zarejestrowane portale
 * (`useOverlayPortalScopeRef`). Funkcja czysta — woła ją i `onKeyDown`, i awaryjny nasłuch.
 */
function collectDragScopes(
  panel: HTMLElement | null,
  portals: ReadonlySet<HTMLElement>,
): HTMLElement[] {
  const scopes = Array.from(portals);
  if (panel) scopes.push(panel);
  return scopes;
}

function OverlayMounted({
  onClose,
  closeOnBackdrop = true,
  closeOnEsc = true,
  role = 'dialog',
  backdropClassName,
  panelClassName,
  labelledBy,
  describedBy,
  ariaLabel,
  testId,
  panelTestId,
  guardUnsaved = false,
  dirty,
  guardResetKey,
  children,
}: OverlayProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  // Portale zarejestrowane przez komponenty w oknie (popover z uchwytami dnd-kit) — razem z panelem
  // wyznaczają, gdzie uchwyt przeciągania „należy do tego okna" (Esc, klik w tło).
  const [portals] = useState(() => new Set<HTMLElement>());
  // Okno zagnieżdżone: Esc z jego portalu bąbelkuje do okna-rodzica przez drzewo React, więc
  // rodzic musi znać także te portale i panel okna zagnieżdżonego (inaczej by się zamknął).
  const parentRegisterPortal = useContext(OverlayPortalScopeContext);
  const registerPortal = useCallback<RegisterOverlayPortal>(
    (element) => {
      portals.add(element);
      const unregisterInParent = parentRegisterPortal?.(element);
      return () => {
        portals.delete(element);
        unregisterInParent?.();
      };
    },
    [portals, parentRegisterPortal],
  );
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel || !parentRegisterPortal) return;
    return parentRegisterPortal(panel);
  }, [parentRegisterPortal]);
  // Element z fokusem z fazy renderu — przed `autoFocus` dzieci (React ustawia go przy commicie).
  const [focusAtRender] = useState<HTMLElement | null>(
    () => document.activeElement as HTMLElement | null,
  );
  // Element wybrany na fokus początkowy — pamiętany, bo StrictMode uruchamia efekt dwa razy.
  const chosenFocusRef = useRef<HTMLElement | null>(null);
  const touchedRef = useRef(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  // Pytanie o porzucenie zmian otwarte → po „Wróć do edycji" trzeba oddać fokus oknu.
  const promptShownRef = useRef(false);

  const requestDismiss = () => {
    const isDirty = dirty ?? touchedRef.current;
    if (guardUnsaved && isDirty) {
      promptShownRef.current = true;
      setConfirmDiscard(true);
    } else {
      onClose();
    }
  };

  // Najświeższe wartości dla stosu okien (efekt rejestracji wykonuje się raz). Aktualizacja
  // synchronicznie przy commicie, żeby awaryjny Esc z `<body>` nie użył przestarzałego
  // `closeOnEsc` / `dirty` w oknie między renderem a efektem pasywnym.
  const latestRef = useRef({ requestDismiss, closeOnEsc });
  useLayoutEffect(() => {
    latestRef.current = { requestDismiss, closeOnEsc };
  });

  useEffect(() => {
    touchedRef.current = false;
  }, [guardResetKey]);

  useEffect(() => {
    if (confirmDiscard || !promptShownRef.current) return;
    promptShownRef.current = false;
    // Pytanie otwarte kliknięciem w tło: fokus był już na `<body>`, więc jego zamknięcie nic nie
    // przywróciło (przy Esc przywraca fokus pytanie samo). Oddajemy fokus panelowi okna.
    const active = document.activeElement;
    if (active === document.body || active === null) panelRef.current?.focus();
  }, [confirmDiscard]);

  useEffect(
    () =>
      registerOverlay({
        getPanel: () => panelRef.current,
        getDragScopes: () => collectDragScopes(panelRef.current, portals),
        isEscapeAllowed: () => latestRef.current.closeOnEsc,
        dismissOnEscape: () => latestRef.current.requestDismiss(),
      }),
    [portals],
  );

  useEffect(() => acquireBodyScrollLock(), []);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    const activeNow = document.activeElement;
    const focusInsidePanel = panel.contains(activeNow);
    // Cel powrotu fokusu: gdy dziecko ustawiło już fokus w panelu (`autoFocus`), to element
    // z fazy renderu; inaczej aktualny — przy zamknięciu okna A i otwarciu okna B w jednym
    // commicie fokus wrócił już do wyzwalacza, a nie do (usuniętego) przycisku okna A.
    const restoreTarget = focusInsidePanel ? focusAtRender : (activeNow as HTMLElement | null);

    if (focusInsidePanel) {
      // `autoFocus` dziecka: zapamiętujemy wybór, bo StrictMode (dev) po symulowanym sprzątaniu
      // oddaje fokus wyzwalaczowi i drugi przebieg efektu inaczej wybrałby pierwszy przycisk (X).
      chosenFocusRef.current = activeNow as HTMLElement;
    } else {
      const previous = chosenFocusRef.current;
      const target =
        previous && previous.isConnected && panel.contains(previous)
          ? previous
          : (panel.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ?? panel);
      chosenFocusRef.current = target;
      target.focus();
    }

    return () => {
      // Synchronicznie (nie rAF/setTimeout): testy przywracania fokusu zależą od kolejności
      // efektów. Nie ruszamy fokusu, gdy użytkownik przeniósł go gdzie indziej albo należy
      // do innego okna na wierzchu.
      const active = document.activeElement;
      const focusFree = active === document.body || active === null || panel.contains(active);
      if (
        restoreTarget &&
        restoreTarget.isConnected &&
        restoreTarget !== document.body &&
        focusFree
      ) {
        restoreTarget.focus();
      }
    };
  }, [focusAtRender]);

  /** Czy trwa przeciąganie dnd-kit z uchwytem w panelu tego okna albo w jego zarejestrowanym portalu. */
  const isOwnDragInProgress = (): boolean =>
    isDragInProgressInScopes(collectDragScopes(panelRef.current, portals));

  const backdropHandlers = useBackdropClose(() => {
    // Klik w tło w trakcie przeciągania (np. klawiaturą w popoverze okna) nie zamyka okna:
    // odmontowanie uchwytu zostawiłoby czujnik dnd-kit aktywny na `document`.
    if (isOwnDragInProgress()) return;
    requestDismiss();
  }, closeOnBackdrop);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const panel = panelRef.current;
    if (e.key === 'Escape') {
      // Trwa przeciąganie (dnd-kit) z uchwytem w tym oknie: Esc anuluje je na `document` — nie
      // zatrzymujemy zdarzenia i nie zamykamy okna. O przynależności decyduje MIEJSCE uchwytu
      // (panel albo zarejestrowany portal), nie cel zdarzenia: fokus mógł przejść z uchwytu
      // gdzie indziej w oknie, a `stopPropagation` poniżej odciąłby wtedy Esc od dnd-kit.
      // Przeciąganie POD oknem albo w obcym portalu nie odbiera oknu Esc.
      if (isOwnDragInProgress()) return;
      // Okno zatrzymuje Esc ZAWSZE: ani rodzic w drzewie, ani skrót strony pod spodem nie
      // zadziała „przez" otwarte okno.
      e.stopPropagation();
      // Esc już obsłużony przez element wewnątrz (np. zamknął listę) — nie zamykamy okna.
      if (e.defaultPrevented) return;
      if (closeOnEsc) {
        e.preventDefault();
        requestDismiss();
      }
      return;
    }
    if (e.key !== 'Tab') return;
    if (!panel) return;
    const focusables = panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    if (focusables.length === 0) {
      e.preventDefault();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (!first || !last) return;
    const active = document.activeElement;
    // Fokus na samym panelu (klik w niefokusowalny obszar okna) liczy się jak „przed pierwszym".
    if (e.shiftKey && (active === first || active === panel)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const markTouched = (e: SyntheticEvent) => {
    if (!guardUnsaved) return;
    const target = e.target;
    if (!(target instanceof Element)) return;
    // Zdarzenia z portali (pole wyszukiwania otwartej listy, zagnieżdżone okno wyboru) bąbelkują
    // do panelu przez drzewo React, ale to nie są dane tego okna.
    if (!panelRef.current?.contains(target)) return;
    if (target.closest('[data-no-dirty]')) return;
    touchedRef.current = true;
  };

  return (
    <>
      {createPortal(
        <div
          role="presentation"
          className={['fixed inset-0', backdropClassName].filter(Boolean).join(' ')}
          data-testid={testId}
          {...backdropHandlers}
          onKeyDown={handleKeyDown}
        >
          <div
            ref={panelRef}
            role={role}
            aria-modal="true"
            aria-label={ariaLabel}
            aria-labelledby={labelledBy}
            aria-describedby={describedBy}
            tabIndex={-1}
            className={panelClassName}
            data-testid={panelTestId}
            onInput={markTouched}
            onChange={markTouched}
          >
            <OverlayPortalScopeContext.Provider value={registerPortal}>
              {children}
            </OverlayPortalScopeContext.Provider>
          </div>
        </div>,
        document.body,
      )}
      {/* Pytanie stoi poza providerem zakresu portali tego okna (rejestruje się u rodzica) —
          bez znaczenia, bo nie ma uchwytów przeciągania i nie pojawia się w trakcie przeciągania. */}
      {confirmDiscard ? (
        <UnsavedGuardPrompt
          onKeepEditing={() => setConfirmDiscard(false)}
          onDiscard={() => {
            setConfirmDiscard(false);
            onClose();
          }}
        />
      ) : null}
    </>
  );
}

/** Pytanie „Porzucić zmiany?" — samodzielne (bez `ConfirmDialog`, który sam stoi na `Modal`). */
function UnsavedGuardPrompt({
  onKeepEditing,
  onDiscard,
}: {
  onKeepEditing: () => void;
  onDiscard: () => void;
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const descId = useId();
  return (
    <Overlay
      open
      onClose={onKeepEditing}
      role="alertdialog"
      backdropClassName="z-50 flex items-center justify-center bg-black/50 p-4"
      panelClassName="w-full max-w-md rounded-2xl border border-border-secondary bg-bg-elevated p-5 shadow-[0_18px_50px_rgba(31,41,55,0.28)] dark:shadow-[0_18px_50px_rgba(0,0,0,0.6)]"
      labelledBy={titleId}
      describedBy={descId}
      testId="unsaved-guard"
    >
      <h2 id={titleId} className="text-lg font-semibold text-text-primary">
        {t('common.unsavedGuard.title')}
      </h2>
      <p id={descId} className="mt-1 text-sm text-text-tertiary">
        {t('common.unsavedGuard.message')}
      </p>
      <div className="mt-4 flex items-center justify-end gap-2">
        <Button variant="secondary" onClick={onKeepEditing} data-testid="unsaved-guard-keep">
          {t('common.unsavedGuard.keepEditing')}
        </Button>
        <Button variant="danger" onClick={onDiscard} data-testid="unsaved-guard-discard">
          {t('common.unsavedGuard.discard')}
        </Button>
      </div>
    </Overlay>
  );
}
